// Stages 04-06 for Canvas 2D — 53 of the 82 chapters render this way.
// Consumes the draw list and nothing else from the engine.
import { STRIDE, X, Y, Z, VALUE, ANGLE, ALPHA } from '../../engine/drawlist.js'
import { resolveInk } from '../../engine/color.js'

const TAU = Math.PI * 2
// Chapter 07: rotation.x = sin(t*0.5)*0.05, rotation.z = cos(t*0.7)*0.03,
// scale = 1 + sin(t*0.5)*0.02. The z amplitude is 0.6 of the x amplitude.
const WOBBLE_RATE = 0.5
const WOBBLE_RATE_Z = 0.7
const WOBBLE_RATIO = 0.6

export const id = 'canvas2d'
export const label = 'Canvas 2D'

export function create(canvas) {
  const ctx = canvas.getContext('2d')
  let W = 0, H = 0, dpr = 1

  // Painter's-algorithm scratch, grown on demand and reused across frames.
  // Canvas 2D has no depth buffer, so once a piece has depth the only way to
  // composite alpha correctly is to draw back to front.
  let orderBuf = new Int32Array(0)
  let depthBuf = new Float32Array(0)

  function resize(width, height, ratio) {
    dpr = Math.min(ratio || 1, 2)
    W = Math.max(1, Math.round(width))
    H = Math.max(1, Math.round(height))
    canvas.width = Math.round(W * dpr)
    canvas.height = Math.round(H * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  // Hard clear vs alpha wash — the difference between a crisp frame and trails.
  function clear(ground, persistence) {
    ctx.globalAlpha = persistence > 0.001 ? Math.max(0.025, 1 - persistence) : 1
    ctx.fillStyle = ground
    ctx.fillRect(0, 0, W, H)
    ctx.globalAlpha = 1
  }

  function depthOrder(d, n, cos, sin, ct, st) {
    if (orderBuf.length < n) {
      orderBuf = new Int32Array(n)
      depthBuf = new Float32Array(n)
    }
    for (let i = 0; i < n; i++) {
      const o = i * STRIDE
      const ry = d[o + X] * sin + d[o + Y] * cos
      depthBuf[i] = ry * st + d[o + Z] * ct
      orderBuf[i] = i
    }
    const view = orderBuf.subarray(0, n)
    view.sort((a, b) => depthBuf[a] - depthBuf[b])
    return view
  }

  function drawLayer({ list, layer, runs, closed }, frame, ink, t = 0) {
    const { mark } = layer
    // Chapter 07's breath: a 2% scale pulse at 0.5 rad/unit.
    const breathe = frame.breathe ? 1 + Math.sin(t * WOBBLE_RATE) * frame.breathe : 1
    const s = Math.min(W, H) * 0.44 * frame.zoom * breathe * (layer.transform.scale || 1)
    const cx = W / 2 + (layer.transform.x || 0) * s
    const cy = H / 2 + (layer.transform.y || 0) * s
    // Orbit revolves the whole piece about its height axis. It composes into
    // the same in-plane rotation everything else already uses, so the depth
    // sort and the mark orientations stay consistent for free.
    // ...and its wobble, one slider driving both axes at chapter 07's own
    // rates and 0.6 amplitude ratio.
    const wob = frame.wobble || 0
    const rot = (frame.rotation || 0) + (frame.orbit || 0) * t
      + (wob ? Math.cos(t * WOBBLE_RATE_Z) * wob * WOBBLE_RATIO : 0)
      + (layer.transform.rotation || 0)
      + (layer.transform.spin || 0) * t
    const cos = Math.cos(rot), sin = Math.sin(rot)

    // 06 · camera. Tilt rotates about the x axis so displacement reads as
    // height; perspective is a bounded divide rather than a real frustum.
    const tilt = (frame.tilt || 0) + (wob ? Math.sin(t * WOBBLE_RATE) * wob : 0)
    const ct = Math.cos(tilt), st = Math.sin(tilt)
    const persp = (frame.perspective || 0) * 0.35
    const dimensional = tilt > 0.001 || persp > 0.001

    // The layer's own colour if it states one, the theme's ink if it does not.
    const paint = resolveInk(layer.ink, ink)
    ctx.fillStyle = paint
    ctx.strokeStyle = paint
    ctx.lineWidth = Math.max(0.4, layer.ink.weight)
    ctx.lineCap = 'round'
    if (mark.geometry === 'glyph') {
      ctx.font = `${(9 * mark.scale + 3).toFixed(1)}px "IBM Plex Mono", ui-monospace, monospace`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
    }

    // The mark's own constant orientation, on top of whatever stage 03 wrote
    // into the angle slot per point.
    const turn = (mark.turn || 0) * TAU
    const sides = Math.max(3, Math.round(mark.sides || 4))
    // Array.from, not indexing the string: a glyph set can hold characters
    // outside the basic plane, and those are two UTF-16 units each.
    const glyphs = Array.from(mark.glyphs && mark.glyphs.length ? mark.glyphs : '/\\|')
    const ramp = mark.glyphMode === 'ramp'
    const d = list.data
    const n = list.count

    // Projection and alpha are shared by the per-mark loop and the path
    // walker below. Written into closure scratch rather than returned, so a
    // point costs no allocation.
    let PX = 0, PY = 0, PK = 1
    const project = (nx, ny, nz) => {
      const rx = nx * cos - ny * sin
      const ry = nx * sin + ny * cos
      const ty = ry * ct - nz * st
      const tz = ry * st + nz * ct
      // Clamped so a point behind the eye magnifies instead of inverting.
      PK = persp > 0 ? 1 / Math.max(0.25, 1 - tz * persp) : 1
      PX = cx + rx * s * PK
      PY = cy + ty * s * PK
    }
    const inv = layer.ink.invert || 0
    const gain = layer.ink.gain
    // Transparency sits on top of everything the field drives.
    const opacity = layer.ink.opacity === undefined ? 1 : layer.ink.opacity
    const vig = frame.vignette > 0 ? frame.vignette : 0
    const bands = layer.ink.bands || 0
    const alphaOf = (o, nx, ny) => {
      const v = d[o + VALUE]
      let drive
      if (bands > 0) {
        // Distance to the nearest level, on the signed value — contours sit at
        // negative levels as well as positive ones.
        const u = v * bands
        drive = 1 - Math.abs(u - Math.round(u)) * 2
      } else {
        // invert 0 reads the peaks, 1 reads the nodes, between blends them.
        const mag = Math.abs(v)
        drive = inv > 0 ? mag * (1 - inv) + (1 - Math.min(1, mag)) * inv : mag
      }
      let a = Math.min(1, drive * gain) * d[o + ALPHA] * opacity
      if (vig > 0) {
        const r = Math.sqrt(nx * nx + ny * ny)
        a *= Math.max(0, 1 - Math.max(0, r - (1 - vig)) / vig)
      }
      return a
    }

    // ── the chain seam, consumed ──────────────────────────────────────────
    // A path mark strokes the substrate's own ordering instead of drawing one
    // shape per sample. Canvas 2D has no per-vertex alpha, so a run has to be
    // broken wherever the ink changes; quantising into bands keeps that from
    // becoming one stroke per segment.
    //
    // It only goes so far. Measured on a 90x90 grid over a busy field, six
    // bands costs a stroke every 1.9 samples and four costs one every 2.6 —
    // the field simply varies faster than the lattice. So a path substrate
    // wants a coarser density than a mark substrate: 40x40 is about 850
    // strokes a frame, 90x90 is about 4300.
    if (mark.geometry === 'path') {
      if (runs) {
        const LEVELS = 6
        let base = 0
        for (let r = 0; r < runs.length; r++) {
          const len = runs[r]
          if (len > 1) {
            let open = false, level = -1
            const steps = closed ? len + 1 : len
            for (let step = 0; step < steps; step++) {
              const o = (base + (step % len)) * STRIDE
              const nx = d[o + X], ny = d[o + Y]
              project(nx, ny, d[o + Z])
              const a = alphaOf(o, nx, ny)
              const lv = a < 0.015 ? 0 : Math.max(1, Math.round(a * LEVELS))
              if (lv === 0) {
                if (open) { ctx.stroke(); open = false }
              } else if (!open) {
                ctx.globalAlpha = lv / LEVELS
                ctx.beginPath(); ctx.moveTo(PX, PY)
                open = true; level = lv
              } else if (lv !== level) {
                // Carry the line to this point, close the band, start the next
                // one here so the stroke stays unbroken across a change.
                ctx.lineTo(PX, PY); ctx.stroke()
                ctx.globalAlpha = lv / LEVELS
                ctx.beginPath(); ctx.moveTo(PX, PY)
                level = lv
              } else {
                ctx.lineTo(PX, PY)
              }
            }
            if (open) ctx.stroke()
          }
          base += len
        }
      }
      ctx.globalAlpha = 1
      return
    }

    // Flat pieces skip the sort entirely — the common case stays as cheap as
    // it was before depth existed.
    const order = dimensional ? depthOrder(d, n, cos, sin, ct, st) : null

    for (let idx = 0; idx < n; idx++) {
      const i = order ? order[idx] : idx
      const o = i * STRIDE
      const nx = d[o + X], ny = d[o + Y], nz = d[o + Z]
      const v = d[o + VALUE]

      const alpha = alphaOf(o, nx, ny)
      if (alpha < 0.015) continue

      project(nx, ny, nz)
      const px = PX, py = PY, k = PK
      const scale = mark.scale * k
      ctx.globalAlpha = alpha

      if (mark.geometry === 'dot') {
        ctx.beginPath()
        ctx.arc(px, py, (0.5 + Math.abs(v) * 3.2) * scale, 0, Math.PI * 2)
        ctx.fill()
      } else if (mark.geometry === 'tick') {
        // Ticks orient in the substrate plane, so the tilt foreshortens the
        // y component exactly as it does position.
        const a = d[o + ANGLE] + rot + turn
        const len = (2.2 + Math.abs(v) * 7) * scale
        const ax = Math.cos(a), ay = Math.sin(a) * ct
        ctx.beginPath()
        ctx.moveTo(px - ax * len, py - ay * len)
        ctx.lineTo(px + ax * len, py + ay * len)
        ctx.stroke()
      } else if (mark.geometry === 'polygon') {
        // Vertices are laid out in the substrate plane and foreshortened by
        // the same ct as ticks and position — so a tilted polygon flattens
        // into the surface instead of staying a billboard facing the camera.
        const a0 = d[o + ANGLE] + rot + turn
        // A polygon has to fill enough of its cell to read as a shape at all —
        // a 4-gon at a dot's radius just looks like a dot. Mostly constant
        // size with the value as modulation, so orientation carries the signal
        // rather than competing with area for it.
        const rad = (2.6 + Math.abs(v) * 7) * scale
        ctx.beginPath()
        for (let k = 0; k < sides; k++) {
          const a = a0 + (k / sides) * TAU
          const vx = px + Math.cos(a) * rad
          const vy = py + Math.sin(a) * rad * ct
          if (k === 0) ctx.moveTo(vx, vy)
          else ctx.lineTo(vx, vy)
        }
        ctx.closePath()
        ctx.fill()
      } else {
        let g
        if (ramp) {
          // -1..1 across the whole set, clamped at both ends.
          const t = Math.min(1, Math.max(0, (v + 1) / 2))
          g = glyphs[Math.min(glyphs.length - 1, Math.floor(t * glyphs.length))]
        } else {
          g = v > 0.1 ? glyphs[0] : v < -0.1 ? glyphs[1] || glyphs[0] : glyphs[2] || glyphs[0]
        }
        ctx.fillText(g, px, py)
      }
    }
    ctx.globalAlpha = 1
  }

  return {
    id, resize, clear, drawLayer,
    dispose() { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, canvas.width, canvas.height) },
  }
}
